import React, { useCallback, useEffect, useRef, useState } from "react";
import Portal from "components/layout/Portal";
import "./DraggableWindow.scoped.scss";

interface IDraggableWindowProps {
  isOpen: boolean;
  header: string;
  initialX?: number;
  initialY?: number;
  onClose(): void;
  children: React.ReactNode;
}

const DraggableWindow: React.FC<IDraggableWindowProps> = ({
  isOpen,
  header,
  initialX = 100,
  initialY = 100,
  onClose,
  children,
}) => {
  const [position, setPosition] = useState({ x: initialX, y: initialY });
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Reset position each time the window is opened.
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: initialX, y: initialY });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return;
    setPosition({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
    });
  }, []);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const onTitleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    e.preventDefault();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        className="draggable-window"
        style={{ left: position.x, top: position.y }}
      >
        <div className="draggable-window-title" onMouseDown={onTitleMouseDown}>
          <span>{header}</span>
          <button className="draggable-window-close" onClick={onClose}>
            &#x2715;
          </button>
        </div>
        <div className="draggable-window-body">{children}</div>
      </div>
    </Portal>
  );
};

export default DraggableWindow;
